import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import type { ListCollaborator, ListInvitation } from "../types/collaboration";
import { logger } from "../lib/logger";
import { notifyListCollaborators, notifyInvitation } from "../lib/notificationService";

// Fetch collaborators for a list
async function fetchCollaborators(listId: string): Promise<ListCollaborator[]> {
  const { data, error } = await supabase.rpc("get_list_collaborators", {
    list_id_param: listId
  });

  if (error) {
    throw error;
  }

  return (data || []) as ListCollaborator[];
}

// Fetch invitations for a list
async function fetchInvitations(listId: string): Promise<ListInvitation[]> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    throw new Error("You must be logged in to view invitations");
  }

  const { data, error } = await supabase
    .from("list_invitations")
    .select("*")
    .eq("list_id", listId)
    .eq("status", "pending")
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []) as ListInvitation[];
}

// Hook to get collaborators
export function useCollaborators(listId: string | null) {
  return useQuery({
    queryKey: ["collaborators", listId],
    queryFn: () => fetchCollaborators(listId!),
    enabled: !!listId, // Only fetch if listId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes (collaborators can change)
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

// Hook to get invitations
export function useInvitations(listId: string | null) {
  return useQuery({
    queryKey: ["invitations", listId],
    queryFn: () => fetchInvitations(listId!),
    enabled: !!listId, // Only fetch if listId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

// Hook to invite a collaborator
export function useInviteCollaborator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { listId: string; email: string }) => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error("You must be logged in to invite collaborators");
      }

      logger.log("🚀 Calling invite-to-list function...");
      logger.log("📧 Email:", params.email);
      logger.log("📋 List ID:", params.listId);

      // Ensure we have a valid session before calling the function
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        logger.error("❌ No valid session:", sessionError);
        throw new Error("Please sign in again");
      }
      logger.log("✅ Session valid, access token present:", !!session.access_token);

      const { data, error } = await supabase.functions.invoke("invite-to-list", {
        body: {
          email: params.email.toLowerCase().trim(),
          listId: params.listId,
          invitedBy: user.id,
        },
      });

      logger.log("📬 Full response:", { data, error });

      if (error) {
        logger.error("❌ Invite function error:", error);
        const errorMessage = data?.error || error.message || "Failed to send invitation";
        throw new Error(errorMessage);
      }

      if (data?.error) {
        logger.error("❌ Response contains error:", data.error);
        throw new Error(data.error);
      }

      logger.log("✅ Invitation sent successfully:", data);
      return { ...data, listId: params.listId, email: params.email, inviterId: user.id };
    },
    onSuccess: (data, variables) => {
      // Invalidate invitations for the list
      queryClient.invalidateQueries({ queryKey: ["invitations", variables.listId] });
      
      // Create notification for invitee (fire and forget)
      if (data?.token) {
        notifyInvitation({
          inviteeEmail: data.email,
          inviterId: data.inviterId,
          listId: data.listId,
          invitationToken: data.token,
          role: "editor",
        });
      }
    }
  });
}

// Hook to accept an invitation
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      logger.log("🔔 useAcceptInvitation called with token:", token);
      
      // Check authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        logger.error("❌ Not authenticated:", authError);
        throw new Error("You must be logged in to accept invitations");
      }
      logger.log("✅ User authenticated:", user.id, user.email);
      
      logger.log("📞 Calling accept_invitation RPC with token:", token);
      const { data, error } = await supabase.rpc("accept_invitation", {
        invitation_token: token
      });

      logger.log("📬 RPC response:", { data, error });

      if (error) {
        logger.error("❌ RPC error:", error);
        throw error;
      }

      if (data && data.success) {
        logger.log("✅ Invitation accepted, listId:", data.list_id);
        return { success: true, listId: data.list_id };
      } else {
        const errorMsg = data?.error || "Failed to accept invitation";
        logger.error("❌ Accept failed:", errorMsg);
        return { success: false, error: errorMsg };
      }
    },
    onSuccess: async (data) => {
      logger.log("🎉 onSuccess callback, data:", data);
      if (data.success && data.listId) {
        // Invalidate lists to show the new list
        queryClient.invalidateQueries({ queryKey: ["lists"] });
        // Invalidate collaborators and invitations for the list
        queryClient.invalidateQueries({ queryKey: ["collaborators", data.listId] });
        queryClient.invalidateQueries({ queryKey: ["invitations", data.listId] });
        // Invalidate notifications
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
        
        // Notify other collaborators that someone joined (fire and forget)
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          notifyListCollaborators({
            listId: data.listId,
            actorId: user.id,
            type: "member_joined",
            title: "New member joined",
            message: `${user.email || "Someone"} joined the list`,
          });
        }
      }
    },
    onError: (error) => {
      logger.error("❌ Mutation error:", error);
    }
  });
}

// Hook to remove a collaborator
export function useRemoveCollaborator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { collaboratorId: string; listId: string }) => {
      logger.log("🗑️ Removing collaborator:", params.collaboratorId);
      
      const { data, error } = await supabase
        .from("list_collaborators")
        .delete()
        .eq("id", params.collaboratorId)
        .select();

      logger.log("📬 Delete response:", { data, error });

      if (error) {
        throw error;
      }

      logger.log("✅ Collaborator removed successfully");
      return params;
    },
    onSuccess: (data) => {
      // Invalidate collaborators for the list
      queryClient.invalidateQueries({ queryKey: ["collaborators", data.listId] });
    }
  });
}

// Hook to cancel an invitation
export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { invitationId: string; listId: string }) => {
      logger.log("🚫 Cancelling invitation:", params.invitationId);
      
      const { data, error } = await supabase.rpc("cancel_invitation", {
        p_invitation_id: params.invitationId
      });

      logger.log("📬 Cancel response:", { data, error });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to cancel invitation");
      }

      logger.log("✅ Invitation cancelled successfully");
      return params;
    },
    onSuccess: (data) => {
      // Invalidate invitations for the list
      queryClient.invalidateQueries({ queryKey: ["invitations", data.listId] });
    }
  });
}

// Hook to decline an invitation
export function useDeclineInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.rpc("decline_invitation", {
        invitation_token: token
      });

      if (error) {
        throw error;
      }

      if (data && data.success) {
        return { success: true };
      } else {
        return { success: false, error: data?.error || "Failed to decline invitation" };
      }
    },
    onSuccess: () => {
      // Invalidate lists and notifications
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
  });
}
